import { Box, Button, Card, HStack, Spinner, Stack, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useParams } from 'react-router'
import { useAgendaSubscription } from '@/hooks/subscriptions/useAgendaSubscription'
import { LuCheck, LuX } from 'react-icons/lu'
import { formatDate } from '@/utils/date'
import { formatMembersLabel, formatStorageLabel } from '../utils'
import PlansModal from './PlansModal'

const FeatureItem = ({ label, enabled }: { label: string; enabled: boolean }) => (
  <HStack gap={2}>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      w={5}
      h={5}
      borderRadius="full"
      bg={enabled ? 'black' : 'gray.200'}
      color={enabled ? 'white' : 'gray.400'}
      flexShrink={0}
    >
      {enabled ? <LuCheck size={12} /> : <LuX size={12} />}
    </Box>
    <Text fontSize="sm" color={enabled ? 'fg' : 'fg.muted'}>
      {label}
    </Text>
  </HStack>
)

const PlanTab = () => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const [plansModalOpen, setPlansModalOpen] = useState(false)

  const { data: subscription, isLoading } = useAgendaSubscription(agendaId!)

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="primary.500" />
      </Box>
    )
  }

  if (!subscription) {
    return (
      <Box py={8} px={4}>
        <Text color="fg.muted" fontSize="sm">
          Nenhum plano encontrado.
        </Text>
      </Box>
    )
  }

  const expiresAt = subscription.expires_at
    ? formatDate(subscription.expires_at)
    : null

  const features = [
    { label: formatMembersLabel(subscription.max_members), enabled: true },
    { label: formatStorageLabel(subscription.max_storage_mb), enabled: true },
    { label: 'Dados financeiros', enabled: subscription.can_view_financial },
  ]

  return (
    <Stack gap={4}>
      <Card.Root variant="outline" w="full">
        <Card.Body gap={4}>
          <Box>
            <Card.Title fontSize="xl">{subscription.plan_name}</Card.Title>
            {expiresAt ? (
              <Text fontSize="sm" color="fg.muted" mt={1}>
                Plano finaliza em{' '}
                <Text as="span" fontWeight="bold" color="fg">
                  {expiresAt}
                </Text>
                {subscription.is_active && (
                  <Text as="span" color="fg.muted">
                    {' '}
                    (Renovação automática)
                  </Text>
                )}
              </Text>
            ) : (
              <Text fontSize="sm" color="fg.muted" mt={1}>
                {subscription.is_free ? 'Plano gratuito' : 'Sem data de expiração'}
              </Text>
            )}
          </Box>

          <Stack gap={2}>
            {features.map((feature) => (
              <FeatureItem key={feature.label} label={feature.label} enabled={feature.enabled} />
            ))}
          </Stack>

          <HStack gap={3} pt={2}>
            <Button variant="outline" size="sm" onClick={() => setPlansModalOpen(true)}>
              Desbloquear mais features
            </Button>
            {!subscription.is_free && (
              <Button variant="outline" size="sm" colorPalette="red">
                Cancelar assinatura
              </Button>
            )}
          </HStack>
        </Card.Body>
      </Card.Root>

      <PlansModal open={plansModalOpen} onOpenChange={setPlansModalOpen} currentPlanId={subscription.plan_id} />
    </Stack>
  )
}

export default PlanTab
